import React, { ReactElement, useEffect, useMemo } from 'react';
import Theme, { DarkBrand, LightBrand } from '../components-library/Theme';
import useConfig from '../hooks/ui/useConfig';
import { ThemeProvider as MuiThemeProvider, createTheme as createMuiTheme } from '@mui/material/styles';
import GlobalStyle from '../styles/GlobalStyles';
import { BorderRadiusStyle, BorderWidthStyle } from '../modules/storefront/exports/Constants';
import { BorderRadius, BorderWidth, ButtonVariant } from '../modules/storefront/exports/Enums';
import useStorefront from '../hooks/storefront/useStorefront';
import { ThemeMode } from '../services/exports/Constants';
import useStorefrontColors from '../modules/storefront/hooks/useStorefrontColors';
import collect from 'collect.js';
import { IButton } from '../modules/storefront/exports/Interfaces';
import { ButtonTypes, IButtonStyle } from '../services/exports/Interfaces';
import { getStorefrontThemeMode } from '../modules/storefront/exports/Cookies';

interface Props {
  children: ReactElement;
}

type ButtonStyles = Record<ButtonTypes, IButtonStyle>;

export type ITheme = typeof Theme & {
  buttons: ButtonStyles;
  isDarkMode: boolean;
};

export const ThemeContext = React.createContext(null);

export default function ThemeProvider({ children }: Props) {
  const { primary_color } = useConfig();
  const { store, isDarkMode } = useStorefront();
  const { getColor } = useStorefrontColors();

  const brand = useMemo(() => (isDarkMode ? DarkBrand : LightBrand), [isDarkMode]);
  const colorVariables = useMemo(
    () => (isDarkMode ? store?.theme?.dark_mode_color_variables : store?.theme?.color_variables),
    [store],
  );
  const primaryColor = useMemo(
    () =>
      getStorefrontThemeMode() === ThemeMode.Dark
        ? '#000000'
        : colorVariables?.primary_color || primary_color || brand.primary,
    [colorVariables?.primary_color, primary_color, brand.primary],
  );

  const storefrontButtons = useMemo(
    () =>
      collect(store?.buttons)
        .whereIn('variant', [ButtonVariant.PrimaryFill, ButtonVariant.SecondaryFill])
        .reduce((acc, button: IButton) => {
          acc[button.variant] = {
            background: getColor(button.background_color),
            text: getColor(button.color),
            border: getColor(button.border_color),
          };
          return acc;
        }, {} as ButtonStyles),
    [store],
  );

  const getThemeColor = (lightModeColor: string, darkModeColor: string): string =>
    isDarkMode ? darkModeColor : lightModeColor;

  const buttonStyles = useMemo(
    () =>
      ({
        [ButtonTypes.primary]: {
          background: getThemeColor(primaryColor, brand.primary),
          text: getThemeColor(Theme.color.white.DEFAULT, Theme.color.black.DEFAULT),
        },
        [ButtonTypes.secondary]: {
          background: getThemeColor(Theme.color.black.DEFAULT, Theme.color.white.DEFAULT),
          text: getThemeColor(Theme.color.white.DEFAULT, Theme.color.black.DEFAULT),
        },
        [ButtonTypes.third]: {
          background: getThemeColor(Theme.color.white[100], Theme.color.black[100]),
          text: brand.text.primary,
          border: getThemeColor(Theme.color.black.DEFAULT, Theme.color.white.DEFAULT),
        },
        [ButtonTypes.forth]: {
          background: getThemeColor(Theme.color.white[100], Theme.color.black[100]),
          text: primaryColor,
          border: primaryColor,
        },
        [ButtonTypes.fifth]: {
          background: undefined,
          text: primaryColor,
          border: brand.border,
        },
        [ButtonTypes.sixth]: {
          background: getThemeColor(Theme.color.gray[400], Theme.color.black[600]),
          text: brand.text.primary,
          border: undefined,
        },
        [ButtonTypes.disabled]: {
          background: brand.disabled.background,
          text: brand.disabled.text,
          border: undefined,
        },
        [ButtonTypes.danger]: {
          background: Theme.color.danger,
          text: Theme.color.white.DEFAULT,
          border: undefined,
        },
        [ButtonTypes.warning]: {
          background: Theme.color.warning,
          text: Theme.color.warning,
          border: undefined,
        },
      }) as ButtonStyles,
    [storefrontButtons, primaryColor, brand, isDarkMode],
  );

  const theme = useMemo(
    () =>
      ({
        ...Theme,
        isDarkMode,
        color: {
          ...Theme.color,
          brand: {
            ...brand,
            primary: getThemeColor(primaryColor, brand.primary),
          },
        },
        border: {
          ...Theme.border,
          radius: BorderRadiusStyle[BorderRadius.Small],
          width: BorderWidthStyle[BorderWidth.Light],
        },
        buttons: buttonStyles,
      }) as ITheme,
    [primaryColor, store, brand, isDarkMode, buttonStyles],
  );

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const muiTheme = createMuiTheme({
    palette: {
      mode: isDarkMode ? ThemeMode.Dark : ThemeMode.Light,
      primary: {
        main: theme.color.brand.primary,
      },
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: Theme.font.size.sm,
            lineHeight: Theme.font.lineHeight.DEFAULT,
            color: theme.color.brand.text.primary,
            borderColor: `${theme.color.brand.border}!important`,
            borderRadius: `${theme.border.radius.lg}!important`,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: Theme.font.size.sm,
            lineHeight: Theme.font.lineHeight.DEFAULT,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: '0px!important',
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            padding: '0px!important',
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={theme}>
      <GlobalStyle theme={theme} />
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
